import { ImportTypeEnum, StagedTrip } from '@/models/gen/graphql';

import Input from '@/components/Input';
import Table from '@/common/Table';
import { createComponentState } from '@/state';

export type ManifestPreviewTableContext = {
  sorting: {
    column: string | undefined;
    direction: 'asc' | 'desc' | undefined;
  };
};
export const useManifestPreviewTable = createComponentState<ManifestPreviewTableContext>({
  sorting: {
    column: 'date',
    direction: 'asc',
  },
});

const ManifestPreviewTableHeader = ({
  data,
  previewType,
  selected,
  onSelect,
  isSelectable,
}: {
  data: StagedTrip[];
  previewType: ImportTypeEnum;
  selected: string[];
  onSelect: (id: string | string[]) => void;
  isSelectable: (row: StagedTrip) => boolean;
}): JSX.Element => {
  const [{ sortBy, sortDirection }, setState] = useManifestPreviewTable(({ state, setState }) => [
    {
      sortBy: state.sorting.column,
      sortDirection: state.sorting.direction,
    },
    setState,
  ]);
  const handleSort = (column: string) => (event) => {
    event.preventDefault();
    event.stopPropagation();
    const isSameColumn = sortBy === column;
    let newDirection: 'asc' | 'desc' | undefined;
    if (!isSameColumn) {
      newDirection = 'asc';
    } else if (sortDirection === 'asc') {
      newDirection = 'desc';
    } else if (sortDirection === 'desc') {
      newDirection = undefined;
    } else {
      newDirection = 'asc';
    }
    setState((current) => ({
      ...current,
      sorting: {
        column,
        direction: newDirection,
      },
    }));
  };
  const sorting = (key: string) => ({ direction: sortBy === key ? sortDirection : undefined, onSort: handleSort(key) });

  const onSelectAll = (): void => {
    onSelect(data.filter(isSelectable).map((row: StagedTrip) => row.id));
  };
  return (
    <Table.Row header>
      <Table.Cell className="SELECT">
        <Input
          type="checkbox"
          onChange={onSelectAll}
          checked={selected.length === data.filter(isSelectable).length && data.filter(isSelectable).length > 0}
        />
      </Table.Cell>
      {previewType === ImportTypeEnum.Addon && (
        <Table.Cell className="FLAGS">
          <i className="sv sv-flag-empty fs-5" />
        </Table.Cell>
      )}
      <Table.Cell className="TYPE" sorting={sorting('typeName')}>
        TYPE
      </Table.Cell>
      <Table.Cell className="DATE" sorting={sorting('scheduled')}>
        DATE
      </Table.Cell>
      <Table.Cell className="SCH" sorting={sorting('scheduled')}>
        SCH
      </Table.Cell>
      {previewType !== ImportTypeEnum.Addon && (
        <Table.Cell className="BUFF" sorting={sorting('bufferMinutes')}>
          BUFF
        </Table.Cell>
      )}
      <Table.Cell className="CITY" sorting={sorting('airportCode')}>
        CITY
      </Table.Cell>
      <Table.Cell className="AL" sorting={sorting('servicerIataAirlineCode')}>
        AL
      </Table.Cell>
      <Table.Cell className="FLT" sorting={sorting('flightNumber')}>
        FLT
      </Table.Cell>
      <Table.Cell className="PLT alternate" sorting={sorting('pilots')}>
        PLT
      </Table.Cell>
      <Table.Cell className="FA alternate" sorting={sorting('attendants')}>
        FA
      </Table.Cell>
      <Table.Cell className="PICKUP" sorting={sorting('puLocation.name')}>
        P/O
      </Table.Cell>
      <Table.Cell className="DROPOFF" sorting={sorting('doLocation.name')}>
        D/O
      </Table.Cell>
      <Table.Cell className="CLT" sorting={sorting('payerProvider.displayName')}>
        CLT
      </Table.Cell>
      <Table.Cell className="STATUS" sorting={sorting('statusName')}>
        STATUS
      </Table.Cell>
    </Table.Row>
  );
};

export default ManifestPreviewTableHeader;
